import { digitalInputStatuses } from 'src/helpers/addons/addon_helpers';
import {
	buildVirtualButtonLabels,
	buildVirtualWebhookLabels,
} from '../view/webhook_virtual_view_helpers';
import { getIlluminationIcon } from 'src/helpers/device_data_helpers';
import { clearQuotes } from '../conditions/webhook_conditions_helpers';
//Types
import type { WebHooksParams } from 'src/types/requests/settings/webhooks';

interface ConditionsEvents {
	'ev.tC'?: number;
	'ev.tF'?: number;
	'ev.rh'?: number;
	'ev.percent'?: number;
	'ev.xpercent'?: number;
	'ev.voltage'?: number;
	'ev.xvoltage'?: number;
	'ev.illumination'?: string;
	'ev.phase'?: string;
	'ev.current'?: number;
	'ev.apower'?: number;
	'ev.total'?: number;
	'ev.xtotal'?: number;
	'ev.freq'?: number;
	'ev.xfreq'?: number;
	'ev.slot'?: string;
	'ev.level'?: number;
	'ev.state'?: boolean;
	'ev.num_objects'?: number;
	'ev.position'?: number;
	'ev.steps'?: number;
	'ev.total_active_power'?: number;
	'ev.total_apparent_power'?: number;
	'ev.total_current'?: number;
	'ev.value'?: boolean | number | string | null;
	'ev.device'?: string;
	'ev.status'?: object;
	'ev.event_data'?: object;
}

export type I18nCtx = {
	translate: (key: string, def: string) => string;
};

export type ConditionToken = '&&' | '||' | string;

export type Condition = {
	event: ConditionsEvents[keyof ConditionsEvents];
	operant: '===' | '!==' | '>' | '<';
	value: boolean | number | string | null;
};

export type ConditionsRule = {
	webhooks: string[] | null;
	when: (cond: Condition) => boolean;
	icon: (hook?: WebHooksParams, cond?: Condition) => string | null;
	iconColor?: (hook?: WebHooksParams) => string | null;
	iconStyle?: (hook?: WebHooksParams) => string | undefined;
	buildComponentLabel?: (hook?: WebHooksParams) => boolean;
	buildEventLabel?: (hook?: WebHooksParams) => string | null;
	conditionlabel: (cond: Condition, ctx?: I18nCtx, hook?: WebHooksParams) => string | null;
};

export function tokenizeCondition(expr: string): ConditionToken[] {
	return (expr || '').split(/\s*(\|\||&&)\s*/g).filter(Boolean);
}

export const CONDITIONS_RULES: ConditionsRule[] = [
	{
		webhooks: [
			'light.on',
			'light.off',
			'rgb.on',
			'rgb.off',
			'rgbw.on',
			'rgbw.off',
			'cct.on',
			'cct.off',
			'rgbcct.on',
			'rgbcct.off',
		],
		when: () => false,
		icon: (hook) => {
			const isOn = /(on)/.test(hook.event);
			const hasColor = /^rgbw?/.test(hook.event);
			if (isOn) {
				return 'fa fa-lightbulb-on';
			}
			if (hasColor) {
				const m = decodeURI(hook?.urls?.toString()).match(/rgb=\[(((\d+(\.\d+)?),?){3})\](&|$)/);
				if (m?.[1]) {
					return 'fa-duotone fa-light fa-lightbulb';
				}
			}
			return 'fa fa-lightbulb';
		},
		iconStyle: (hook) => {
			const isOn = /(on)/.test(hook.event);
			if (isOn) return undefined;

			if (/^rgbw?/.test(hook.event)) {
				const m = decodeURI(hook?.urls?.toString()).match(/rgb=\[(((\d+(\.\d+)?),?){3})\](&|$)/);
				if (m?.[1]) {
					const c = m[1].split(',');
					const color = `rgb(${c[0]},${c[1]},${c[2]})`;
					return `--fa-primary-color: ${color}; --fa-secondary-color: ${color}; --fa-secondary-opacity: 1`;
				}
			}
			return undefined;
		},
		iconColor: (hook) => {
			if (/^rgbw?/.test(hook.event)) {
				const m = decodeURI(hook?.urls?.toString()).match(/rgb=\[(((\d+(\.\d+)?),?){3})\](&|$)/);
				if (m?.[1]) {
					const c = m[1].split(',');
					return `rgb(${c[0]},${c[1]},${c[2]})`;
				}
			}
			return null;
		},
		conditionlabel: () => null,
	},
	{
		webhooks: ['switch.on', 'switch.off', 'input.toggle_on', 'input.toggle_off'],
		when: () => false,
		icon: (hook) => {
			if (hook.icon) {
				const digitalIcon = digitalInputStatuses().find(
					(i) => i.icon_on == hook.icon || i.icon_off == hook.icon,
				);
				if (digitalIcon) {
					return digitalIcon[`icon_${/(on)/.test(hook.event) ? 'on' : 'off'}`];
				}
			}
			return `fas fa-toggle-${/(off)/.test(hook.event) ? 'off' : 'on'}`;
		},
		conditionlabel: () => null,
	},
	{
		webhooks: ['switch.active_power_change', 'switch.active_power_measurement'],
		when: (cond) => cond.event === 'ev.apower',
		icon: () => 'fas fa-gauge-circle-bolt',
		conditionlabel: (cond) => {
			return `${cond.operant} ${cond.value} W`;
		},
	},
	{
		webhooks: [
			'input.button_push',
			'input.button_longpush',
			'input.button_doublepush',
			'input.button_triplepush',
			'input.touch_multi_touch',
			'input.touch_swipe_down',
			'input.touch_swipe_up',
		],
		when: () => false,
		icon: () => 'far fa-hand-back-point-up',
		conditionlabel: () => null,
	},
	{
		webhooks: ['input.analog_change', 'input.analog_measurement'],
		when: (cond) => cond.event === 'ev.percent',
		icon: () => 'far fa-gauge-low',
		conditionlabel: (cond) => {
			return `${cond.operant} ${cond.value} %`;
		},
	},
	{
		webhooks: ['input.count_change', 'input.count_measurement'],
		when: (cond) => /^ev\.(x?total)$/.test(cond.event as string),
		icon: (hook) => hook.icon || 'far fa-gauge-low',
		conditionlabel: (cond, _ctx, hook) => {
			const unit = cond.event === 'ev.xtotal' ? hook.xunit : hook.unit;
			return `${cond.operant} ${cond.value} ${unit}`;
		},
	},
	{
		webhooks: ['input.freq_change', 'freq_measurement'],
		when: (cond) => /^ev\.(x?freq)$/.test(cond.event as string),
		icon: () => 'fas fa-wave-square',
		conditionlabel: (cond, _ctx, hook) => {
			const unit = cond.event === 'ev.xfreq' ? hook.freqXUnit : hook.freqUnit;
			return `${cond.operant} ${cond.value} ${unit}`;
		},
	},
	{
		webhooks: ['temperature.change', 'temperature.measurement', 'blutrv.temperature_change'],
		when: (cond) => /^ev\.t(C|F)$/.test(cond.event as string),
		icon: () => 'fa fa-thermometer-three-quarters',
		conditionlabel: (cond) => {
			const unit = cond.event === 'ev.tC' ? '°C' : '°F';
			return `${cond.operant} ${cond.value}${unit}`;
		},
	},
	{
		webhooks: ['humidity.change', 'humidity.measurement'],
		when: (cond) => cond.event === 'ev.rh',
		icon: () => 'fa fa-humidity',
		conditionlabel: (cond) => `${cond.operant} ${cond.value} %`,
	},
	{
		webhooks: ['voltmeter.change', 'voltmeter.measurement'],
		when: (cond) => /^ev\.(x?voltage)$/.test(cond.event as string),
		icon: (hook) => hook.icon || 'far fa-gauge-low',
		conditionlabel: (cond, _ctx, hook) => {
			const unit = cond.event === 'ev.xvoltage' ? hook.xunit : hook.unit;
			return `${cond.operant} ${cond.value} ${unit}`;
		},
	},
	{
		webhooks: ['smoke.alarm', 'smoke.alarm_off', 'smoke.alarm_test'],
		when: () => null,
		icon: (hook) => {
			return `fa-solid fa-fire-smoke${hook.event === 'smoke.alarm' ? '' : ' text-danger'}`;
		},
		conditionlabel: () => null,
	},
	{
		webhooks: ['flood.alarm', 'flood.alarm_off'],
		when: () => null,
		icon: (hook) => {
			return `fas fa-house-flood-water${hook.event === 'flood.alarm' ? ' text-danger' : ''}`;
		},
		conditionlabel: () => null,
	},
	{
		webhooks: ['cover.open', 'cover.closed', 'cover.opening', 'cover.closing'],
		when: () => false,
		icon: (hook) => `far fa-blinds${/(clos)/.test(hook.event) ? '' : '-raised'}`,
		conditionlabel: () => null,
	},
	{
		webhooks: ['illuminance.change', 'illuminance.measurement'],
		when: (cond) => cond.event === 'ev.illumination' || cond.event === 'ev.lux',
		icon: (_hook, cond) => {
			return `fas ${getIlluminationIcon(clearQuotes((cond?.value || '') as string))}`;
		},
		conditionlabel: (cond, ctx) => {
			const state = clearQuotes(cond.value as string);
			let label = ctx.translate(`alarm_state_${state}`, state);
			if (cond.operant === '!==') {
				label = ctx.translate('condition_is_not', 'is not') + ' ' + label;
			}
			return label;
		},
	},
	{
		webhooks: [
			'em.voltage_change',
			'em.current_change',
			'em.active_power_change',
			'em1.voltage_change',
			'em1.current_change',
			'em1.active_power_change',
			'pm1.voltage_change',
			'pm1.current_change',
			'pm1.active_power_change',
			'em.total_active_power_change',
			'em.total_apparent_power_change',
			'em.total_current_change',
		],
		when: (cond) => {
			return (
				cond.event === 'ev.phase' ||
				cond.event === 'ev.voltage' ||
				cond.event === 'ev.current' ||
				cond.event === 'ev.act_power' ||
				cond.event === 'ev.total_active_power' ||
				cond.event === 'ev.total_apparent_power' ||
				cond.event === 'ev.total_current'
			);
		},
		icon: () => 'fas fa-gauge-circle-bolt',
		conditionlabel: (cond, _ctx, hook) => {
			if (cond.event === 'ev.phase') {
				const operant = cond.operant.replace('===', '~').replace('!==', '!~');
				return `${operant} ${clearQuotes(cond.value as string).toUpperCase()}`;
			}
			const unit =
				cond.event === 'ev.voltage'
					? 'V'
					: /(current)/.test(hook.event)
						? 'A'
						: /(apparent)/.test(hook.event)
							? 'VA'
							: 'W';
			return `${cond.operant} ${cond.value} ${unit}`;
		},
	},
	{
		webhooks: [
			'cb.on',
			'cb.off',
			'cb.undervoltage',
			'cb.overvoltage',
			'cb.safety_enable',
			'cb.safety_disable',
		],
		when: () => null,
		icon: (hook) => {
			if (/^(cb.on|cb.off)$/.test(hook.event)) {
				return `fas fa-toggle-${/(off)/.test(hook.event) ? 'off' : 'on'}`;
			}
			return null;
		},
		conditionlabel: () => null,
	},
	{
		webhooks: ['cury.level_changed'],
		when: (cond) => cond.event === 'ev.slot' || cond.event === 'ev.level',
		icon: () => 'fas fa-spray-can',
		conditionlabel: (cond, ctx) => {
			let operant = '';
			if (cond.operant === '!==') {
				operant = ctx.translate('condition_is_not', 'is not') + ' ';
			}
			if (cond.event === 'ev.level') {
				return ctx.translate('label_liquid_level', 'Liquid Level') + ` ${operant}${cond.value}%`;
			} else if (cond.event === 'ev.slot') {
				return (
					operant +
					(clearQuotes(cond.value as string) === 'left'
						? ctx.translate('label_intensity_left', 'Intensity left')
						: ctx.translate('label_intensity_right', 'Intensity right'))
				);
			}
		},
	},
	{
		webhooks: [
			'presencezone.presence',
			'presencezone.counter',
			'presencezone.enter',
			'presencezone.leave',
		],
		when: (cond) => cond.event === 'ev.state' || cond.event === 'ev.num_objects',
		icon: (hook, cond) => {
			switch (hook.event) {
				case 'presencezone.presence':
					return `fas fa-person${/^true$/.test(cond?.value as string) ? '-walking' : ''}`;
				case 'presencezone.counter':
					return 'fas fa-user-friends';
				case 'presencezone.enter':
					return 'fas fa-person-walking';
				case 'presencezone.leave':
					return 'fas fa-person';
			}
		},
		buildComponentLabel: () => true,
		conditionlabel: (cond, ctx) => {
			if (cond.event === 'ev.state') {
				return /^true$/.test(cond.value as string)
					? ctx.translate('label_occupied', 'Occupied')
					: ctx.translate('label_not_occupied', 'Not occupied');
			}
			return `${cond.operant} ${cond.value}`;
		},
	},
	{
		webhooks: ['camera.motion', 'camera.motion_end', 'camerazone.motion', 'camerazone.motion_end'],
		when: () => false,
		icon: (hook) => {
			switch (hook.event) {
				case 'camera.motion':
				case 'camerazone.motion':
					return 'fas fa-person-walking';
				case 'camera.motion_end':
				case 'camerazone.motion_end':
					return 'fas fa-person';
				default:
					return 'fas fa-video';
			}
		},
		buildComponentLabel: () => true,
		conditionlabel: () => null,
	},
	{
		webhooks: [
			'boolean.change',
			'enum.change',
			'number.change',
			'text.change',
			'bthomesensor.value_change',
			'bthomesensor.state_change',
		],
		when: (cond) => cond.event === 'ev.value',
		icon: (hook, cond) => {
			const virtualLabels = buildVirtualWebhookLabels(hook, cond);
			return virtualLabels?.icon || hook.icon;
		},
		conditionlabel: (cond, _ctx, hook) => {
			const virtualLabels = buildVirtualWebhookLabels(hook, cond);
			return virtualLabels?.label || '';
		},
	},
	{
		webhooks: ['blutrv.position_change'],
		when: (cond) => cond.event === 'ev.position',
		icon: () => 'fas fa-pipe-valve',
		conditionlabel: (cond) => {
			return `${cond.operant} ${cond.value} %`;
		},
	},
	{
		webhooks: ['lnm.rx_status', 'lnm.rx_event'],
		when: (cond) => cond.event === 'ev.device',
		icon: () => 'fas fa-network-wired',
		conditionlabel: (cond, ctx) => {
			const operant =
				cond.operant === '!=='
					? ctx.translate('condition_is_not', 'is not')
					: ctx.translate('condition_is', 'is');
			return `${ctx.translate('process_device', 'Device')} ${operant} ${clearQuotes(cond.value as string)}`;
		},
	},
	{
		webhooks: ['lnm.rx_status', 'lnm.rx_event'],
		when: (cond) => /^ev\.(status|event_data)(\.|\[)/.test(cond.event as string),
		icon: () => 'fas fa-network-wired',
		conditionlabel: (cond) => `${cond.event} ${cond.operant} ${cond.value}`,
	},
	{
		webhooks: [
			'bthomesensor.single_push',
			'bthomesensor.double_push',
			'bthomesensor.triple_push',
			'bthomesensor.long_push',
			'bthomedevice.single_push',
			'bthomedevice.double_push',
			'bthomedevice.triple_push',
			'bthomedevice.long_push',
			'button.single_push',
			'button.double_push',
			'button.triple_push',
			'button.long_push',
		],
		when: (cond) => cond.event === 'ev.idx' || cond.event === 'ev.steps',
		icon: () => 'far fa-hand-back-point-up',
		buildEventLabel: (hook) => {
			const channel =
				hook?.components?.filter((i) => i?.config?.obj_id == 58)?.length > 1
					? `${Number(hook.cid) - 200}`
					: '';
			return buildVirtualButtonLabels(hook, channel).label;
		},
		conditionlabel: (cond, _ctx, hook) => {
			return buildVirtualButtonLabels(hook, cond.value as string).label;
		},
	},
];
